import {
  RaidPlanActor,
  RaidPlanAssignmentEntity,
} from 'app/frontend/types/custom/RaidPlan'

type AggregateOverrideEntity = Extract<
  RaidPlanAssignmentEntity,
  { kind: 'everyone' | 'collection' | 'group' }
>

export type EncounterAssignmentEntityOverride =
  | {
      kind: 'participant'
      characterId: number
    }
  | {
      kind: 'aggregate'
      entity: AggregateOverrideEntity
    }
  | {
      kind: 'ignored'
    }

export type EncounterAssignmentAbilityOverride = {
  hidden?: boolean
  casts?: Record<
    string,
    {
      boxes?: Record<
        string,
        {
          entities?: Record<string, EncounterAssignmentEntityOverride>
        }
      >
    }
  >
}

export type EventPlanEncounterAssignmentOverrides = {
  abilities?: Record<string, EncounterAssignmentAbilityOverride>
}

export type EventPlanCastTargetOverrides = {
  casts?: Record<
    string,
    {
      entities?: Record<string, EncounterAssignmentEntityOverride>
    }
  >
}

export type EventPlanActorOverrides = {
  actors?: Record<
    string,
    {
      characterId?: number
      replacementActor?: RaidPlanActor
      ignored?: boolean
    }
  >
}

export type VisualEntitySlotOverride =
  | EncounterAssignmentEntityOverride
  | { kind: 'empty' }

export type EventPlanVisualEntityOverrides = Record<
  string,
  VisualEntitySlotOverride
>

export type EventPlanOverridesByPlanId = Record<
  string,
  {
    encounterAssignments?: EventPlanEncounterAssignmentOverrides
    castTargetOverrides?: EventPlanCastTargetOverrides
    actorOverrides?: EventPlanActorOverrides
    visualEntityOverrides?: EventPlanVisualEntityOverrides
  }
>

type SetEncounterAssignmentEntityOverrideInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  abilityKey: string
  castIds: string[]
  boxId: string
  entityId: string
  override: EncounterAssignmentEntityOverride
}

type GetEncounterAssignmentEntityOverrideCharacterIdInput = {
  encounterAssignments?: EventPlanEncounterAssignmentOverrides
  abilityKey: string
  castIds: string[]
  boxId?: string
  entityId?: string
}

type GetEncounterAssignmentEntityOverrideInput = {
  encounterAssignments?: EventPlanEncounterAssignmentOverrides
  abilityKey: string
  castIds: string[]
  boxId?: string
  entityId?: string
}

type SetEncounterAssignmentAbilityHiddenInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  abilityKey: string
  hidden: boolean
}

type SetActorOverrideCharacterIdInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
  characterId: number
}

type SetActorOverrideReplacementActorInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
  replacementActor: RaidPlanActor
}

type GetActorOverrideCharacterIdInput = {
  actorOverrides?: EventPlanActorOverrides
  actorUuid: string
}

type GetActorOverrideReplacementActorInput = {
  actorOverrides?: EventPlanActorOverrides
  actorUuid: string
}

type SetActorOverrideIgnoredInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
  ignored: boolean
}

type GetActorOverrideIgnoredInput = {
  actorOverrides?: EventPlanActorOverrides
  actorUuid: string
}

type ClearActorOverrideReplacementActorInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
}

type ClearActorOverrideIgnoredInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
}

type ClearActorOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
}

type ClearCastTargetOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
}

type ClearEncounterAssignmentAbilityOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  abilityKey: string
}

type SetCastTargetEntityOverrideInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  castId: string
  entityId: string
  override: EncounterAssignmentEntityOverride
}

type GetCastTargetEntityOverrideInput = {
  castTargetOverrides?: EventPlanCastTargetOverrides
  castId: string
  entityId: string
}

type GetEncounterAssignmentAbilityHiddenInput = {
  encounterAssignments?: EventPlanEncounterAssignmentOverrides
  abilityKey: string
}

type SetVisualEntityOverrideInput = {
  planOverrides: EventPlanOverridesByPlanId
  visualPlanId: number
  slotKey: string
  override: VisualEntitySlotOverride
}

type SetVisualEntityOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  visualPlanId: number
  visualEntityOverrides: EventPlanVisualEntityOverrides
}

type GetVisualEntityOverrideInput = {
  visualEntityOverrides?: EventPlanVisualEntityOverrides
  slotKey: string
}

type ClearVisualEntityOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  visualPlanId: number
}

type VisualEntityOverridesAfterSelectInput = {
  visualEntityOverrides: EventPlanVisualEntityOverrides
  slotAssignments: { slotKey: string; characterId?: number }[]
  clickedSlotKey: string
  override: EncounterAssignmentEntityOverride
}

export const getPlanEncounterAssignmentOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  planId: number | undefined,
) => {
  if (!planId) return undefined
  return planOverrides?.[planId.toString()]?.encounterAssignments
}

export const getPlanActorOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  planId: number | undefined,
) => {
  if (!planId) return undefined
  return planOverrides?.[planId.toString()]?.actorOverrides
}

export const getPlanCastTargetOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  planId: number | undefined,
) => {
  if (!planId) return undefined
  return planOverrides?.[planId.toString()]?.castTargetOverrides
}

export const getActorOverrideCharacterId = ({
  actorOverrides,
  actorUuid,
}: GetActorOverrideCharacterIdInput) => {
  return actorOverrides?.actors?.[actorUuid]?.characterId
}

export const getActorOverrideReplacementActor = ({
  actorOverrides,
  actorUuid,
}: GetActorOverrideReplacementActorInput) => {
  return actorOverrides?.actors?.[actorUuid]?.replacementActor
}

export const getActorOverrideIgnored = ({
  actorOverrides,
  actorUuid,
}: GetActorOverrideIgnoredInput) => {
  return actorOverrides?.actors?.[actorUuid]?.ignored === true
}

export const setActorOverrideCharacterId = ({
  planOverrides,
  planId,
  actorUuid,
  characterId,
}: SetActorOverrideCharacterIdInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingActorOverrides = existingPlan.actorOverrides ?? {}
  const existingActors = existingActorOverrides.actors ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: {
            ...existingActors[actorUuid],
            characterId,
          },
        },
      },
    },
  }
}

export const setActorOverrideReplacementActor = ({
  planOverrides,
  planId,
  actorUuid,
  replacementActor,
}: SetActorOverrideReplacementActorInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingActorOverrides = existingPlan.actorOverrides ?? {}
  const existingActors = existingActorOverrides.actors ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: {
            ...existingActors[actorUuid],
            replacementActor,
          },
        },
      },
    },
  }
}

export const setActorOverrideIgnored = ({
  planOverrides,
  planId,
  actorUuid,
  ignored,
}: SetActorOverrideIgnoredInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingActorOverrides = existingPlan.actorOverrides ?? {}
  const existingActors = existingActorOverrides.actors ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: {
            ...existingActors[actorUuid],
            ignored,
          },
        },
      },
    },
  }
}

export const clearActorOverrideReplacementActor = ({
  planOverrides,
  planId,
  actorUuid,
}: ClearActorOverrideReplacementActorInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.actorOverrides?.actors?.[actorUuid]) return planOverrides
  const existingActorOverrides = existingPlan.actorOverrides
  const existingActors = existingActorOverrides.actors ?? {}
  const existingActor = existingActors[actorUuid]
  if (!existingActor?.replacementActor) return planOverrides
  const nextActor = {
    ...existingActor,
    replacementActor: undefined,
  }
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: nextActor,
        },
      },
    },
  }
}

export const clearActorOverrideIgnored = ({
  planOverrides,
  planId,
  actorUuid,
}: ClearActorOverrideIgnoredInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.actorOverrides?.actors?.[actorUuid]) return planOverrides
  const existingActorOverrides = existingPlan.actorOverrides
  const existingActors = existingActorOverrides.actors ?? {}
  const existingActor = existingActors[actorUuid]
  if (!existingActor?.ignored) return planOverrides
  const nextActor = {
    ...existingActor,
    ignored: undefined,
  }
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: nextActor,
        },
      },
    },
  }
}

export const clearActorOverrides = ({
  planOverrides,
  planId,
}: ClearActorOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.actorOverrides) return planOverrides

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: undefined,
    },
  }
}

export const clearCastTargetOverrides = ({
  planOverrides,
  planId,
}: ClearCastTargetOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.castTargetOverrides) return planOverrides

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      castTargetOverrides: undefined,
    },
  }
}

export const setEncounterAssignmentEntityOverride = ({
  planOverrides,
  planId,
  abilityKey,
  castIds,
  boxId,
  entityId,
  override,
}: SetEncounterAssignmentEntityOverrideInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const normalizedAbilityKey = abilityKey.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingEncounterAssignments = existingPlan.encounterAssignments ?? {}
  const existingAbilities = existingEncounterAssignments.abilities ?? {}
  const existingAbility = existingAbilities[normalizedAbilityKey] ?? {}
  const existingCasts = existingAbility.casts ?? {}

  const nextCasts = castIds.reduce<typeof existingCasts>(
    (acc, castId) => {
      const existingCast = existingCasts[castId] ?? {}
      const existingBoxes = existingCast.boxes ?? {}
      const existingBox = existingBoxes[boxId] ?? {}
      const existingEntities = existingBox.entities ?? {}
      acc[castId] = {
        ...existingCast,
        boxes: {
          ...existingBoxes,
          [boxId]: {
            ...existingBox,
            entities: {
              ...existingEntities,
              [entityId]: override,
            },
          },
        },
      }
      return acc
    },
    { ...existingCasts },
  )

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      encounterAssignments: {
        ...existingEncounterAssignments,
        abilities: {
          ...existingAbilities,
          [normalizedAbilityKey]: {
            ...existingAbility,
            casts: nextCasts,
          },
        },
      },
    },
  }
}

export const getEncounterAssignmentEntityOverrideCharacterId = ({
  encounterAssignments,
  abilityKey,
  castIds,
  boxId,
  entityId,
}: GetEncounterAssignmentEntityOverrideCharacterIdInput) => {
  const override = getEncounterAssignmentEntityOverride({
    encounterAssignments,
    abilityKey,
    castIds,
    boxId,
    entityId,
  })
  if (!override || override.kind !== 'participant') return undefined
  return override.characterId
}

export const getEncounterAssignmentEntityOverride = ({
  encounterAssignments,
  abilityKey,
  castIds,
  boxId,
  entityId,
}: GetEncounterAssignmentEntityOverrideInput) => {
  if (!boxId || !entityId) return undefined
  const ability = encounterAssignments?.abilities?.[abilityKey.toString()]
  if (!ability?.casts) return undefined
  for (const castId of castIds) {
    const override = ability.casts[castId]?.boxes?.[boxId]?.entities?.[entityId]
    if (override) return override
  }
  return undefined
}

export const setEncounterAssignmentAbilityHidden = ({
  planOverrides,
  planId,
  abilityKey,
  hidden,
}: SetEncounterAssignmentAbilityHiddenInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const normalizedAbilityKey = abilityKey.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingEncounterAssignments = existingPlan.encounterAssignments ?? {}
  const existingAbilities = existingEncounterAssignments.abilities ?? {}
  const existingAbility = existingAbilities[normalizedAbilityKey] ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      encounterAssignments: {
        ...existingEncounterAssignments,
        abilities: {
          ...existingAbilities,
          [normalizedAbilityKey]: {
            ...existingAbility,
            hidden,
          },
        },
      },
    },
  }
}

export const clearEncounterAssignmentAbilityOverrides = ({
  planOverrides,
  planId,
  abilityKey,
}: ClearEncounterAssignmentAbilityOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const normalizedAbilityKey = abilityKey.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingEncounterAssignments = existingPlan.encounterAssignments ?? {}
  const existingAbilities = existingEncounterAssignments.abilities ?? {}

  if (!existingAbilities[normalizedAbilityKey]) return planOverrides

  const nextAbilities = { ...existingAbilities }
  delete nextAbilities[normalizedAbilityKey]

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      encounterAssignments: {
        ...existingEncounterAssignments,
        abilities: nextAbilities,
      },
    },
  }
}

export const setCastTargetEntityOverride = ({
  planOverrides,
  planId,
  castId,
  entityId,
  override,
}: SetCastTargetEntityOverrideInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingCastTargetOverrides = existingPlan.castTargetOverrides ?? {}
  const existingCasts = existingCastTargetOverrides.casts ?? {}
  const existingCast = existingCasts[castId] ?? {}
  const existingEntities = existingCast.entities ?? {}
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      castTargetOverrides: {
        ...existingCastTargetOverrides,
        casts: {
          ...existingCasts,
          [castId]: {
            ...existingCast,
            entities: {
              ...existingEntities,
              [entityId]: override,
            },
          },
        },
      },
    },
  }
}

export const getCastTargetEntityOverride = ({
  castTargetOverrides,
  castId,
  entityId,
}: GetCastTargetEntityOverrideInput) => {
  return castTargetOverrides?.casts?.[castId]?.entities?.[entityId]
}

export const getEncounterAssignmentAbilityHidden = ({
  encounterAssignments,
  abilityKey,
}: GetEncounterAssignmentAbilityHiddenInput) =>
  encounterAssignments?.abilities?.[abilityKey.toString()]?.hidden === true

export const visualPlanOverridesKey = (visualPlanId: number) =>
  `visual:${visualPlanId}`

export const visualEntitySlotKey = (
  screenId: string,
  shapeId: string,
  localIndex: number,
) => `${screenId}:${shapeId}:${localIndex}`

export const getPlanVisualEntityOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  visualPlanId: number | undefined,
) => {
  if (!visualPlanId) return undefined
  return planOverrides?.[visualPlanOverridesKey(visualPlanId)]
    ?.visualEntityOverrides
}

export const getVisualEntityOverride = ({
  visualEntityOverrides,
  slotKey,
}: GetVisualEntityOverrideInput) => visualEntityOverrides?.[slotKey]

export const setVisualEntityOverride = ({
  planOverrides,
  visualPlanId,
  slotKey,
  override,
}: SetVisualEntityOverrideInput): EventPlanOverridesByPlanId => {
  const planKey = visualPlanOverridesKey(visualPlanId)
  const existingPlan = planOverrides[planKey] ?? {}
  const existingVisualEntityOverrides = existingPlan.visualEntityOverrides ?? {}
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      visualEntityOverrides: {
        ...existingVisualEntityOverrides,
        [slotKey]: override,
      },
    },
  }
}

export const setVisualEntityOverrides = ({
  planOverrides,
  visualPlanId,
  visualEntityOverrides,
}: SetVisualEntityOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = visualPlanOverridesKey(visualPlanId)
  const existingPlan = planOverrides[planKey] ?? {}
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      visualEntityOverrides,
    },
  }
}

export const clearVisualEntityOverrides = ({
  planOverrides,
  visualPlanId,
}: ClearVisualEntityOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = visualPlanOverridesKey(visualPlanId)
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.visualEntityOverrides) return planOverrides
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      visualEntityOverrides: undefined,
    },
  }
}

export const hasVisualEntityOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  visualPlanId: number | undefined,
) => {
  const overrides = getPlanVisualEntityOverrides(planOverrides, visualPlanId)
  return overrides !== undefined && Object.keys(overrides).length > 0
}

export const visualEntityOverridesAfterSelect = ({
  visualEntityOverrides,
  slotAssignments,
  clickedSlotKey,
  override,
}: VisualEntityOverridesAfterSelectInput): EventPlanVisualEntityOverrides => {
  if (override.kind === 'aggregate' || override.kind === 'ignored') {
    return {
      ...visualEntityOverrides,
      [clickedSlotKey]: override,
    }
  }

  const clicked = slotAssignments.find(
    (assignment) => assignment.slotKey === clickedSlotKey,
  )
  if (clicked?.characterId === override.characterId) {
    return visualEntityOverrides
  }

  const nextOverrides: EventPlanVisualEntityOverrides = {
    ...visualEntityOverrides,
    [clickedSlotKey]: override,
  }
  const other = slotAssignments.find(
    (assignment) =>
      assignment.slotKey !== clickedSlotKey &&
      assignment.characterId === override.characterId,
  )
  if (!other) return nextOverrides

  if (clicked?.characterId != null) {
    nextOverrides[other.slotKey] = {
      kind: 'participant',
      characterId: clicked.characterId,
    }
  } else {
    nextOverrides[other.slotKey] = { kind: 'empty' }
  }
  return nextOverrides
}
